import { combineReducers } from "redux";
import user from "./user";
import accomplishments from "./accomplishments";
import account, * as fromAccount from "./account";
import activities, * as fromActivities from "./activities";
import activityTypes from "./activityTypes";
import applications, * as fromApplications from "./applications";
import appraisees, * as fromAppraisees from "./appraisees";
import appraisers from "./appraisers";
import appraisals, * as fromAppraisals from "./appraisals";
import attendances from "./attendances";
import categories from "./categories";
import contacts from "./contacts";
import courses, * as fromCourses from "./courses";
import jobPlans, * as fromJobPlans from "./jobPlans";
import errors, * as fromErrors from "./errors";
import events, * as fromEvents from "./events";
import facilitators from "./facilitators";
import fetching, * as fromFetching from "./fetching";
import launchURLs, * as fromLaunchURLs from "./launchURLs";
import library, * as fromLibrary from "./library";
import messages, * as fromMessages from "./messages";
import mfaMethods from "./mfaMethods";
import notes, * as fromNotes from "./notes";
import notifications, * as fromNotifications from "./notifications";
import pdps, * as fromPDPs from "./pdps";
import developmentNeeds, * as fromDevelopmentNeeds from "./developmentNeeds";
import qualifications, * as fromQualifications from "./qualifications";
import questionnaires, * as fromQuestionnaires from "./questionnaires";
import recipients from "./recipients";
import resources, * as fromResources from "./resources";
import successMessage from "./successMessage";
import surveys, * as fromSurveys from "./surveys";
import surveyTypes from "./surveyTypes";
import surveyReports, * as fromSurveyReports from "./surveyReports";
import tasks from "./tasks";
import terms from "./terms";
import { ActionTypes } from "../constants";
import { destroyState } from "../utilities/stateFunctions";

const mainReducer = combineReducers({
    user,
    accomplishments,
    account,
    activities,
    activityTypes,
    applications,
    appraisees,
    appraisers,
    appraisals,
    attendances,
    categories,
    contacts,
    courses,
    jobPlans,
    errors,
    events,
    facilitators,
    fetching,
    launchURLs,
    library,
    messages,
    mfaMethods,
    notes,
    notifications,
    pdps,
    developmentNeeds,
    qualifications,
    recipients,
    resources,
    successMessage,
    surveys,
    surveyReports,
    surveyTypes,
    questionnaires,
    tasks,
    terms,
});

const store = (state, action) => {
    if (action.type === ActionTypes.LOGOUT) {
        // reset the whole store on logout; undefined means the store will reset the values to their default
        destroyState();
        state = getPersistedState(state);
    }
    return mainReducer(state, action);
};

export default store;
export const getPersistedState = ({ user, account, applications }) => ({
    user,
    account,
    applications,
});

export const getUser = ({user}) => user;
export const getSubscriptions = ({account}) => fromAccount.getSubscriptions(account);
export const getProfile = ({account}) => fromAccount.getProfile(account);
export const getAccomplishments = ({accomplishments}) => accomplishments;
export const getActivities = ({activities}) => fromActivities.getActivities(activities);
export const getActivity = ({activities}, activityId) => fromActivities.getActivity(activities, activityId);
export const getActivityTypes = ({activityTypes}) => activityTypes;
export const getApplications = ({applications}) => applications;
export const getApplication = ({applications}, appCode) => fromApplications.getApplication(applications, appCode);
export const getApplicationCodes = ({applications}) => fromApplications.getCodes(applications);
export const getRmsAppCode = ({applications}) => fromApplications.getRmsAppCode(applications);
export const isAppInactive = ({applications}, appCode, uuid) => fromApplications.isInactive(applications, appCode, uuid);
export const getMfaMethods = ({mfaMethods}) => mfaMethods;

export const getAppraisees = ({appraisees}) => appraisees;
export const getAppraisee = ({appraisees}, uuid) => fromAppraisees.getAppraisee(appraisees, uuid);

export const getAppraisers = ({appraisers}) => appraisers;
export const getAppraisals = ({appraisals}) => fromAppraisals.getAppraisals(appraisals);
export const getAppraisal = (appraisalId, { appraisals }) => !appraisals ? {} : appraisals.find(({id}) => id === appraisalId);
export const getCategories = ({categories}) => categories;
export const getCourses = ({courses}) => courses;
export const getCourseDetails = ({courses}, courseId) => fromCourses.getCourse(courses, courseId);

export const getCategoryCourses = ({courses}, categoryId) => fromCourses.getByCategory(courses, categoryId);
export const getMyCourses = ({courses}) => fromCourses.getByEnrolments(courses);
export const getMyAttendances = ({attendances}) => attendances;
export const getEvents = ({events}) => events;
export const getEvent = ({events}, eventId) => fromEvents.getEvent(events, eventId);
export const getLaunchURL = ({launchURLs}, moduleId) => fromLaunchURLs.getByModule(launchURLs, moduleId);
export const getLaunchURLError = ({launchURLs}, moduleId) => fromLaunchURLs.getErrors(launchURLs, moduleId);

export const getJobPlans = ({jobPlans}) => fromJobPlans.getJobPlans(jobPlans);
export const getJobPlan = ({jobPlans}, jobPlanId) => fromJobPlans.getJobPlan(jobPlans, jobPlanId);
export const getJobPlanActivityCategories = ({jobPlans}) => fromJobPlans.getActivityCategories(jobPlans);
export const getJobPlanActivityTypes = ({jobPlans}) => fromJobPlans.getActivityTypes(jobPlans);
export const getJobPlanActivityType = ({jobPlans}, id) => fromJobPlans.getActivityType(jobPlans, id);
export const getJobPlanActivityLocations = ({jobPlans}) => fromJobPlans.getActivityLocations(jobPlans);
export const getJobPlanActivityObjectives = ({jobPlans}) => fromJobPlans.getActivityObjectives(jobPlans);

export const getLibrary = ({library}) => fromLibrary.getLibrary(library);
export const getLibraryCategory = ({library}, categoryId) => fromLibrary.getCategory(library, categoryId);
export const getContacts = ({contacts}) => contacts;
export const getMessages = ({messages}) => messages;
export const getInboxMessages = ({messages}) => fromMessages.getInboxMessages(messages);
export const getMessage = ({messages}, messageId) => fromMessages.getMessage(messages, messageId);
export const getNotes = ({notes}) => fromNotes.getNotes(notes);
export const getNote = ({notes}, noteId) => fromNotes.getNote(notes, noteId);
export const getUploadedNoteId = ({notes}) => fromNotes.getUploadedNoteId(notes);
export const getNotifications = ({notifications}) => notifications;
export const getNotification = ({notifications}, notificationId) => fromNotifications.getNotification(notifications, notificationId);
export const getPDPs = ({pdps}) => fromPDPs.getPDPs(pdps);
export const getPDP = ({pdps}, pdpId) => fromPDPs.getPDP(pdps, pdpId);
export const getDevelopmentNeeds = ({developmentNeeds}) => developmentNeeds;
export const getDevelopmentNeed = ({developmentNeeds}, developmentNeedId) => fromDevelopmentNeeds.getDevelopmentNeed(developmentNeeds, developmentNeedId);

export const getSurveys = ({surveys}) => surveys;
export const getSurvey = ({surveys}, surveyId) => fromSurveys.getSurvey(surveys, surveyId);
export const getSurveyReport = ({surveyReports}, surveyId) => fromSurveyReports.getBySurvey(surveyReports, surveyId);
export const getSurveyTypes = ({surveyTypes}) => surveyTypes;
export const getQuestionnaires = ({questionnaires}) => questionnaires;
export const getQuestionnaire = ({questionnaires}, questionnaireId) => fromQuestionnaires.getQuestionnaire(questionnaires, questionnaireId);
export const getFacilitators = ({facilitators}) => facilitators;
export const getRecipients = ({recipients}) => recipients;

export const getQualifications = ({qualifications}) => qualifications;
export const getQualification = ({qualifications}, qualificationId) => fromQualifications.getQualification(qualifications, qualificationId);
export const getResources = ({resources}) => fromResources.getResources(resources);
export const getResource = ({resources}, resourceId) => fromResources.getResource(resources, resourceId);
export const getUploadedResourceId = ({resources}) => fromResources.getUploadedResourceId(resources);
export const getTasks = ({tasks}) => tasks;
export const getTerms = ({terms}) => terms;
export const getTokens = ({tokens}) => tokens;

export const getErrors = ({errors}) => errors;
export const getError = ({errors}, actionType) => fromErrors.getError(errors, actionType);
export const getSuccessMessage = ({successMessage}) => successMessage;
export const isFetching = ({fetching}, dataType, id = null) => fromFetching.isFetching(fetching, dataType, id);
export const isSending = ({fetching}, dataType) => fromFetching.isSending(fetching, dataType);
